import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Turnstile from "react-turnstile";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Field, TextArea, PillToggle } from "@/components/Form";
import { Eyebrow, Headline } from "@/components/Editorial";
import ProgressBar from "@/components/ProgressBar";
import RightDrawer from "@/components/RightDrawer";
import { Spinner } from "@/components/Loader";
import useMarketingPrograms from "@/lib/useMarketingPrograms";
import { toast } from "@/components/Toast";
import { affPost } from "@/lib/affiliateApi";
import { useAuth } from "@/lib/auth";
import { titleCase, formatPhoneUS, phoneToE164 } from "@/lib/format";
import { C } from "@/lib/brand";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const AUDIENCE_SIZES = [
  { value: "lt_5k", label: "Under 5,000", estimate: 2500 },
  { value: "5k_25k", label: "5,000 — 25,000", estimate: 15000 },
  { value: "25k_100k", label: "25,000 — 100,000", estimate: 62500 },
  { value: "100k_500k", label: "100,000 — 500,000", estimate: 300000 },
  { value: "gt_500k", label: "500,000+", estimate: 750000 },
];

const PLATFORMS = [
  "Instagram",
  "TikTok",
  "YouTube",
  "Podcast",
  "Newsletter",
  "Blog/Website",
  "X/Twitter",
  "LinkedIn",
  "Other",
];

const TOPICS = [
  "Wellness",
  "Fitness",
  "Nutrition",
  "Weight loss",
  "Longevity",
  "Recovery",
  "Skincare",
  "Mental health",
  "Biohacking",
  "General health",
  "Other",
];

const initial = {
  // Step 1
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  // Step 2
  audienceSize: "",
  primaryPlatforms: [],
  audienceTopics: [],
  // Step 3
  socialHandles: { instagram: "", tiktok: "", youtube: "", podcast: "", website: "", other: "" },
  contentSamples: ["", "", ""],
  // Step 4
  whyVoya9: "",
  promotionPlan: "",
  termsAccepted: false,
  expectationsAcknowledged: false,
  // Bot defense
  turnstileToken: "",
};

export default function AffiliateApply() {
  const navigate = useNavigate();
  const { consumer, bootstrapped } = useAuth();
  // When the member is signed in, prefill basics from their profile
  // and skip Step 1 entirely — we already captured those at signup
  // and re-asking feels like the site forgot who they are. Deferred
  // until auth bootstrap completes so refresh-on-page doesn't briefly
  // render Step 1 with empty fields before the consumer arrives.
  const isAuthed = !!consumer;
  const seededInitial = useMemo(() => {
    if (!isAuthed) return initial;
    return {
      ...initial,
      firstName: consumer.firstName || "",
      lastName: consumer.lastName || "",
      email: consumer.email || "",
      phone: consumer.phone || "",
    };
  }, [isAuthed, consumer]);

  const [step, setStep] = useState(isAuthed ? 2 : 1);
  const [data, setData] = useState(seededInitial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // If the consumer arrives AFTER first render (refresh path), reseed
  // the form once and bump past Step 1.
  useEffect(() => {
    if (bootstrapped && isAuthed) {
      setData((prev) => ({
        ...prev,
        firstName: prev.firstName || consumer.firstName || "",
        lastName: prev.lastName || consumer.lastName || "",
        email: prev.email || consumer.email || "",
        phone: prev.phone || consumer.phone || "",
      }));
      setStep((s) => (s === 1 ? 2 : s));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapped, isAuthed, consumer?.email]);

  const total = isAuthed ? 3 : 4;
  // Display step labels — when signed in we show 1–3 (we skipped basics)
  // so the UI doesn't say "Step 02 / 03" out of the gate.
  const displayStep = isAuthed ? Math.max(1, step - 1) : step;
  const displayTotal = total;
  const update = (patch) => setData((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    // Scroll to top on every step change.
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, submitted]);

  if (submitted) {
    return <AffiliateApplySubmitted email={data.email} />;
  }

  const gotoNext = () => {
    const err = validateStep(step, data);
    if (err) {
      toast(err, "error");
      return;
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    const err = validateStep(4, data);
    if (err) {
      toast(err, "error");
      return;
    }
    setSubmitting(true);
    try {
      // Backend currently persists audienceSize as an INTEGER (mid-bucket
      // estimate). We still send the original bucket string in
      // `audienceSizeBucket` so the BE can switch which one it stores
      // without a frontend redeploy. If neither field exists the BE
      // will simply ignore the extra key.
      const bucket = AUDIENCE_SIZES.find((b) => b.value === data.audienceSize);
      const audienceSizeInt = bucket?.estimate ?? 0;
      await affPost(
        "/affiliate/apply",
        {
          firstName: titleCase(data.firstName),
          lastName: titleCase(data.lastName),
          email: data.email.trim().toLowerCase(),
          phone: phoneToE164(data.phone),
          audienceSize: audienceSizeInt,
          audienceSizeBucket: data.audienceSize,
          primaryPlatforms: data.primaryPlatforms,
          audienceTopics: data.audienceTopics,
          socialHandles: data.socialHandles,
          contentSamples: data.contentSamples.filter((s) => s.trim().length > 0),
          whyVoya9: data.whyVoya9.trim(),
          promotionPlan: data.promotionPlan.trim(),
          termsAccepted: data.termsAccepted,
          expectationsAcknowledged: data.expectationsAcknowledged,
          turnstileToken: data.turnstileToken,
          // When the applicant is a signed-in member, link the
          // application back to their consumer record so ops can
          // dedupe and operators can credit affiliate commissions to
          // an existing account without manual reconciliation.
          ...(isAuthed && (consumer?._id || consumer?.id) ? { consumerId: consumer._id || consumer.id } : {}),
        },
        { auth: false },
      );
      setSubmitted(true);
    } catch (err) {
      // Surface a readable message instead of pretending success.
      // The previous fallback silently set `submitted` on any non-4xx
      // failure — that hid real backend errors and made members think
      // they'd applied when nothing was persisted.
      const msg = formatApiError(err);
      toast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="affiliate-apply-page" style={{ minHeight: "100vh", background: C.ink, color: C.cream }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px" }}>
        <Link to="/affiliates">
          <Logo light size={22} />
        </Link>
        <Link
          to={isAuthed ? "/app/me" : "/affiliates"}
          data-testid="affiliate-apply-close"
          style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.5)", textDecoration: "none" }}
        >
          CLOSE ✕
        </Link>
      </header>

      <ProgressBar current={displayStep} total={displayTotal} />

      <div style={{ maxWidth: 620, margin: "0 auto", padding: "48px 24px 120px" }}>
        {isAuthed && (
          <div
            data-testid="apply-signed-in-banner"
            style={{
              background: "rgba(244,201,178,0.08)",
              border: `1px solid rgba(244,201,178,0.18)`,
              padding: "14px 16px",
              marginBottom: 28,
              display: "flex",
              gap: 14,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.peach, whiteSpace: "nowrap" }}>
              ★ APPLYING AS
            </span>
            <span style={{ flex: 1, fontSize: 13, color: C.cream, minWidth: 0 }}>
              <strong style={{ fontWeight: 700 }}>{titleCase(consumer.firstName || "")} {titleCase(consumer.lastName || "")}</strong>
              {consumer.email && (
                <span style={{ color: "rgba(250,246,240,0.55)" }}> · {consumer.email}</span>
              )}
            </span>
            <Link
              to="/app/me"
              style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: "rgba(250,246,240,0.7)", textDecoration: "none", whiteSpace: "nowrap" }}
            >
              EDIT ACCOUNT →
            </Link>
          </div>
        )}
        {step === 1 && <Step1Basics data={data} update={update} displayStep={displayStep} displayTotal={displayTotal} />}
        {step === 2 && <Step2Audience data={data} update={update} displayStep={displayStep} displayTotal={displayTotal} />}
        {step === 3 && <Step3Work data={data} update={update} displayStep={displayStep} displayTotal={displayTotal} />}
        {step === 4 && <Step4Final data={data} update={update} displayStep={displayStep} displayTotal={displayTotal} />}

        <div style={{ marginTop: 40, display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center" }}>
          {step > (isAuthed ? 2 : 1) ? (
            <button
              type="button"
              data-testid="apply-back-btn"
              onClick={() => setStep((s) => s - 1)}
              style={{
                background: "transparent",
                border: 0,
                color: "rgba(250,246,240,0.7)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.2em",
                cursor: "pointer",
              }}
            >
              ← BACK
            </button>
          ) : (
            <span />
          )}

          {step < 4 ? (
            <Button variant="peach" size="lg" testId="apply-continue-btn" onClick={gotoNext}>
              CONTINUE →
            </Button>
          ) : (
            <Button
              variant="peach"
              size="lg"
              testId="apply-submit-btn"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? <Spinner /> : "SUBMIT →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Build the "STEP NN / MM" eyebrow string from the dynamic step
// numbers. When the member is signed in we skip Step 1 (basics),
// so the visible labels are 01–03 instead of 01–04.
function stepLabel(displayStep, displayTotal) {
  return `★ STEP ${String(displayStep).padStart(2, "0")} / ${String(displayTotal).padStart(2, "0")}`;
}

/* ============================================================= */
/*  Step 1 — Basics                                               */
/* ============================================================= */
function Step1Basics({ data, update, displayStep, displayTotal }) {
  return (
    <>
      <Eyebrow color={C.peach}>{stepLabel(displayStep, displayTotal)}</Eyebrow>
      <Headline before="THE" italic="basics." color={C.cream} italicColor={C.peach} size={56} className="mt-6" />
      <p style={{ marginTop: 16, color: "rgba(250,246,240,0.7)", fontSize: 16, lineHeight: 1.6 }}>
        Let's start with how to reach you.
      </p>

      <div style={{ background: C.cream, color: C.ink, marginTop: 28, padding: 32 }}>
        <Field
          label="FIRST NAME"
          value={data.firstName}
          onChange={(v) => update({ firstName: v })}
          onBlur={() => update({ firstName: titleCase(data.firstName) })}
          placeholder="Sarah"
          testId="apply-firstname"
          required
          autoFocus
        />
        <Field
          label="LAST NAME"
          value={data.lastName}
          onChange={(v) => update({ lastName: v })}
          onBlur={() => update({ lastName: titleCase(data.lastName) })}
          placeholder="Jones"
          testId="apply-lastname"
          required
        />
        <Field
          label="EMAIL ADDRESS"
          type="email"
          value={data.email}
          onChange={(v) => update({ email: v })}
          placeholder="sarah@example.com"
          testId="apply-email"
          required
        />
        <Field
          label="PHONE (OPTIONAL)"
          type="tel"
          value={formatPhoneUS(data.phone) || data.phone}
          onChange={(v) => update({ phone: v })}
          placeholder="(555) 123-4567"
          testId="apply-phone"
        />
      </div>
    </>
  );
}

/* ============================================================= */
/*  Step 2 — Audience                                             */
/* ============================================================= */
function Step2Audience({ data, update, displayStep, displayTotal }) {
  const togglePlatform = (p) => {
    const set = new Set(data.primaryPlatforms);
    set.has(p) ? set.delete(p) : set.add(p);
    update({ primaryPlatforms: Array.from(set) });
  };
  const toggleTopic = (t) => {
    const set = new Set(data.audienceTopics);
    set.has(t) ? set.delete(t) : set.add(t);
    update({ audienceTopics: Array.from(set) });
  };

  return (
    <>
      <Eyebrow color={C.peach}>{stepLabel(displayStep, displayTotal)}</Eyebrow>
      <Headline before="YOUR" italic="audience." color={C.cream} italicColor={C.peach} size={56} className="mt-6" />
      <p style={{ marginTop: 16, color: "rgba(250,246,240,0.7)", fontSize: 16, lineHeight: 1.6 }}>
        Who do you reach, and where?
      </p>

      <div style={{ background: C.cream, color: C.ink, marginTop: 28, padding: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
          APPROXIMATE AUDIENCE SIZE
        </div>
        <div
          style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}
          className="!grid-cols-1 md:!grid-cols-2"
        >
          {AUDIENCE_SIZES.map((s) => (
            <button
              key={s.value}
              type="button"
              data-testid={`apply-audience-${s.value}`}
              onClick={() => update({ audienceSize: s.value })}
              style={{
                padding: "14px 18px",
                background: data.audienceSize === s.value ? C.ink : C.pure,
                color: data.audienceSize === s.value ? C.cream : C.ink,
                border: `1.5px solid ${data.audienceSize === s.value ? C.ink : C.warmGray3}`,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "left",
                minWidth: 0,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div style={{ marginTop: 32, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
          PRIMARY PLATFORMS (SELECT ALL)
        </div>
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10 }}>
          {PLATFORMS.map((p) => (
            <PillToggle
              key={p}
              label={p}
              active={data.primaryPlatforms.includes(p)}
              onClick={() => togglePlatform(p)}
              testId={`apply-platform-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}
            />
          ))}
        </div>

        <div style={{ marginTop: 32, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
          YOUR AUDIENCE TOPICS (SELECT ALL)
        </div>
        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10 }}>
          {TOPICS.map((t) => (
            <PillToggle
              key={t}
              label={t}
              active={data.audienceTopics.includes(t)}
              onClick={() => toggleTopic(t)}
              testId={`apply-topic-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

/* ============================================================= */
/*  Step 3 — Work                                                 */
/* ============================================================= */
function Step3Work({ data, update, displayStep, displayTotal }) {
  const setHandle = (k, v) => update({ socialHandles: { ...data.socialHandles, [k]: v } });
  const setSample = (i, v) => {
    const next = [...data.contentSamples];
    next[i] = v;
    update({ contentSamples: next });
  };

  return (
    <>
      <Eyebrow color={C.peach}>{stepLabel(displayStep, displayTotal)}</Eyebrow>
      <Headline before="YOUR" italic="work." color={C.cream} italicColor={C.peach} size={56} className="mt-6" />
      <p style={{ marginTop: 16, color: "rgba(250,246,240,0.7)", fontSize: 16, lineHeight: 1.6 }}>
        Share your handles and a few representative posts. Fill only what applies.
      </p>

      <div style={{ background: C.cream, color: C.ink, marginTop: 28, padding: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
          SOCIAL HANDLES & URLS
        </div>
        <div style={{ marginTop: 4 }}>
          <Field label="INSTAGRAM" value={data.socialHandles.instagram} onChange={(v) => setHandle("instagram", v)} placeholder="@sarahjones" testId="apply-handle-instagram" />
          <Field label="TIKTOK" value={data.socialHandles.tiktok} onChange={(v) => setHandle("tiktok", v)} placeholder="@sarahjones" testId="apply-handle-tiktok" />
          <Field label="YOUTUBE" value={data.socialHandles.youtube} onChange={(v) => setHandle("youtube", v)} placeholder="youtube.com/@sarahjones" testId="apply-handle-youtube" />
          <Field label="PODCAST" value={data.socialHandles.podcast} onChange={(v) => setHandle("podcast", v)} placeholder="Podcast name / URL" testId="apply-handle-podcast" />
          <Field label="WEBSITE" value={data.socialHandles.website} onChange={(v) => setHandle("website", v)} placeholder="https://sarahjones.com" testId="apply-handle-website" />
          <Field label="OTHER" value={data.socialHandles.other} onChange={(v) => setHandle("other", v)} placeholder="LinkedIn, Substack, etc." testId="apply-handle-other" />
        </div>

        <div style={{ marginTop: 32, fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray }}>
          CONTENT SAMPLES (OPTIONAL · PASTE 2–3 URLS)
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 12, color: C.warmGray, lineHeight: 1.5, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          Skip if your work lives entirely on social — your handles above are enough.
        </p>
        <div style={{ marginTop: 4 }}>
          {[0, 1, 2].map((i) => (
            <Field
              key={i}
              label={`SAMPLE ${i + 1}`}
              value={data.contentSamples[i]}
              onChange={(v) => setSample(i, v)}
              placeholder="https://…"
              testId={`apply-sample-${i}`}
            />
          ))}
        </div>
      </div>
    </>
  );
}

/* ============================================================= */
/*  Step 4 — Final                                                */
/* ============================================================= */
function Step4Final({ data, update, displayStep, displayTotal }) {
  return (
    <>
      <Eyebrow color={C.peach}>{stepLabel(displayStep, displayTotal)}</Eyebrow>
      <Headline before="THE FINAL" italic="piece." color={C.cream} italicColor={C.peach} size={56} className="mt-6" />
      <p style={{ marginTop: 16, color: "rgba(250,246,240,0.7)", fontSize: 16, lineHeight: 1.6 }}>
        Tell us why VOYA9, and how you'd plan to promote it.
      </p>

      <div style={{ background: C.cream, color: C.ink, marginTop: 28, padding: 32 }}>
        <TextArea
          label="WHY ARE YOU INTERESTED IN PROMOTING VOYA9?"
          value={data.whyVoya9}
          onChange={(v) => update({ whyVoya9: v })}
          placeholder="A sentence or two is plenty."
          rows={5}
          testId="apply-why"
        />
        <div style={{ marginTop: 4, fontSize: 12, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
          {countWords(data.whyVoya9)} {countWords(data.whyVoya9) === 1 ? "word" : "words"}
        </div>

        <TextArea
          label="HOW DO YOU PLAN TO PROMOTE VOYA9?"
          value={data.promotionPlan}
          onChange={(v) => update({ promotionPlan: v })}
          placeholder="Describe your content approach."
          rows={4}
          testId="apply-plan"
        />

        <div style={{ marginTop: 28 }}>
          <CheckboxRow
            testId="apply-terms"
            checked={data.termsAccepted}
            onToggle={() => update({ termsAccepted: !data.termsAccepted })}
            label={
              <>
                I've read and agree to the{" "}
                <Link to="/legal/terms" target="_blank" style={{ color: C.peachDark, textDecoration: "underline" }}>
                  affiliate program terms
                </Link>
                .
              </>
            }
          />
          <CheckboxRow
            testId="apply-expectations"
            checked={data.expectationsAcknowledged}
            onToggle={() => update({ expectationsAcknowledged: !data.expectationsAcknowledged })}
            label="I understand approval is not guaranteed and reviews take up to 5 business days."
          />
        </div>

        <div style={{ marginTop: 24, padding: "14px 16px", border: `1.5px dashed ${C.warmGray3}`, display: "flex", gap: 14, alignItems: "center", justifyContent: "center", minHeight: 70 }}>
          {TURNSTILE_SITE_KEY ? (
            <>
              <Turnstile
                sitekey={TURNSTILE_SITE_KEY}
                onVerify={(token) => update({ turnstileToken: token })}
                onExpire={() => update({ turnstileToken: "" })}
                onError={() => update({ turnstileToken: "" })}
                theme="light"
                size="flexible"
              />
              {!data.turnstileToken && (
                <div style={{ fontSize: 12, color: C.warmGray, fontStyle: "italic" }}>Verifying you're human…</div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, color: C.warmGray }}>Turnstile not configured.</div>
          )}
        </div>
      </div>
    </>
  );
}

function CheckboxRow({ checked, onToggle, label, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onToggle}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "12px 0",
        background: "transparent",
        border: 0,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          border: `1.5px solid ${C.ink}`,
          background: checked ? C.ink : C.pure,
          color: C.cream,
          fontSize: 14,
          fontWeight: 800,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {checked ? "✓" : ""}
      </span>
      <span style={{ fontSize: 14, color: C.ink, lineHeight: 1.55 }}>{label}</span>
    </button>
  );
}

/* ============================================================= */
/*  Submitted confirmation                                        */
/* ============================================================= */
function AffiliateApplySubmitted({ email }) {
  const [drawer, setDrawer] = useState(null); // null | "guidelines" | "agreement" | "latest"
  const programs = useMarketingPrograms();
  // Pick the most recently published / launched program. Sort defensively
  // — the BE has shifted field names before. Prefer explicit launch date,
  // fall back to publishedAt or updatedAt, then leave order untouched.
  const latest = (() => {
    if (!Array.isArray(programs) || programs.length === 0) return null;
    const dateOf = (p) => {
      const d = p?.launchedAt || p?.launchDate || p?.publishedAt || p?.updatedAt || p?.lastEditedAt;
      return d ? new Date(d).getTime() : 0;
    };
    const sorted = [...programs].sort((a, b) => dateOf(b) - dateOf(a));
    return sorted[0] || null;
  })();
  return (
    <div data-testid="affiliate-apply-submitted" style={{ minHeight: "100vh", background: C.cream, color: C.ink }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px" }}>
        <Link to="/affiliates">
          <Logo size={22} />
        </Link>
      </header>
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "88px 24px 120px" }}>
        <div
          aria-hidden
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: C.sage,
            color: C.ink,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 34,
            fontWeight: 800,
          }}
        >
          ✓
        </div>
        <Eyebrow color={C.peachDark}>★ APPLICATION SUBMITTED</Eyebrow>
        <Headline before="WE'RE" italic="on it." size={80} className="mt-6" />
        <p style={{ marginTop: 28, fontSize: 17, lineHeight: 1.65, color: C.warmGray }}>
          Thank you for applying to the VOYA9 affiliate program. We review every application personally and will get
          back to you within 5 business days{email ? <> at <strong style={{ color: C.ink }}>{email}</strong></> : null}.
        </p>

        <div style={{ marginTop: 36, padding: 28, background: C.bone }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.warmGray, marginBottom: 12 }}>
            ★ WHILE YOU WAIT
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12, fontSize: 14, lineHeight: 1.6 }}>
            <li>
              <button
                type="button"
                onClick={() => setDrawer("guidelines")}
                data-testid="while-you-wait-guidelines"
                style={{ background: "transparent", border: 0, padding: 0, color: C.ink, textDecoration: "underline", textUnderlineOffset: 3, fontSize: 14, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
              >
                Program guidelines &amp; FAQ →
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => setDrawer("agreement")}
                data-testid="while-you-wait-agreement"
                style={{ background: "transparent", border: 0, padding: 0, color: C.ink, textDecoration: "underline", textUnderlineOffset: 3, fontSize: 14, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
              >
                Brand voice &amp; affiliate agreement →
              </button>
            </li>
            {latest && (
              <li>
                <button
                  type="button"
                  onClick={() => setDrawer("latest")}
                  data-testid="while-you-wait-latest"
                  style={{ background: "transparent", border: 0, padding: 0, color: C.ink, textDecoration: "underline", textUnderlineOffset: 3, fontSize: 14, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                >
                  Latest from VOYA9 — <em style={{ fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400 }}>{latest.name || latest.title}</em> →
                </button>
              </li>
            )}
          </ul>
        </div>

        <div style={{ marginTop: 36 }}>
          <Link to="/" data-testid="apply-back-home" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.ink }}>
            ← BACK TO MAIN SITE
          </Link>
        </div>
      </div>

      <RightDrawer
        open={drawer === "guidelines"}
        onClose={() => setDrawer(null)}
        eyebrow="★ WHILE YOU WAIT"
        title="Program guidelines & FAQ"
        testId="drawer-guidelines"
      >
        <ProgramGuidelinesContent />
      </RightDrawer>
      <RightDrawer
        open={drawer === "agreement"}
        onClose={() => setDrawer(null)}
        eyebrow="★ WHILE YOU WAIT"
        title="Brand voice & affiliate agreement"
        testId="drawer-agreement"
      >
        <BrandVoiceAgreementContent />
      </RightDrawer>
      <RightDrawer
        open={drawer === "latest"}
        onClose={() => setDrawer(null)}
        eyebrow="★ LATEST FROM VOYA9"
        title={latest?.name || latest?.title || "Latest program"}
        testId="drawer-latest"
      >
        {latest && <LatestProgramContent program={latest} />}
      </RightDrawer>
    </div>
  );
}

// Curated preview of the most recently launched program. Goal: warm
// the applicant up so they understand the cadence + voice they'll be
// promoting if approved. Keep this informational, not salesy — we're
// not asking them to buy, we're seeding their understanding.
function LatestProgramContent({ program }) {
  const Section = ({ heading, children }) => (
    <section style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: C.peachDeep, marginBottom: 10 }}>
        ★ {heading.toUpperCase()}
      </h3>
      <div style={{ fontSize: 14.5, lineHeight: 1.65, color: C.ink }}>{children}</div>
    </section>
  );
  const summary =
    program.summary ||
    program.subtitle ||
    program.tagline ||
    program.shortDescription ||
    program.description;
  const bullets =
    (Array.isArray(program.highlights) && program.highlights) ||
    (Array.isArray(program.benefits) && program.benefits) ||
    (Array.isArray(program.bullets) && program.bullets) ||
    [];
  const launched = program.launchedAt || program.publishedAt;
  return (
    <div>
      {launched && (
        <p style={{ marginTop: 0, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 14, color: C.warmGray }}>
          Launched {new Date(launched).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </p>
      )}
      {summary && (
        <Section heading="The pitch">
          {summary}
        </Section>
      )}
      {bullets.length > 0 && (
        <Section heading="What members get">
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {bullets.slice(0, 6).map((b, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                {typeof b === "string" ? b : (b?.text || b?.label || "")}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section heading="How affiliates promote this">
        Once you're approved, the program playbook in your affiliate dashboard will have audience-fit notes, sample posts, FAQ responses, and the disclaimers required for compounded compounds. We don't expect you to write from scratch — we expect you to make it sound like you.
      </Section>
      <p style={{ marginTop: 28, fontSize: 12, color: C.warmGray }}>
        Want the full program page?{" "}
        <Link
          to={`/programs/${program.slug || program.programSlug}`}
          style={{ color: C.peachDeep, textDecoration: "underline" }}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in a new tab →
        </Link>
      </p>
    </div>
  );
}

// Static guidance — keeps the affiliate engaged without forcing them
// to navigate away from the success page. Kept short on purpose; the
// full agreement lives at /legal/terms which we still link out to at
// the bottom for the read-the-whole-thing crowd.
function ProgramGuidelinesContent() {
  const Section = ({ heading, children }) => (
    <section style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: C.peachDeep, marginBottom: 10 }}>
        ★ {heading.toUpperCase()}
      </h3>
      <div style={{ fontSize: 14.5, lineHeight: 1.65, color: C.ink }}>{children}</div>
    </section>
  );
  return (
    <div>
      <p style={{ marginTop: 0, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 17, lineHeight: 1.6, color: C.warmGray }}>
        A quick orientation while we review your application.
      </p>
      <Section heading="What we approve">
        We approve creators whose audience trusts them. We care about voice and integrity, not follower count. Honest, specific, lived-in content out-performs polished testimonials every time.
      </Section>
      <Section heading="Commission structure">
        20% revenue share on the first 12 months of a member's subscription, paid monthly via Stripe Connect. Self-purchases and family discounts are excluded. Refunds clawback proportionally.
      </Section>
      <Section heading="What you can't do">
        No medical claims you can't substantiate. No "cures" or "guaranteed results." No before/after photos of medical conditions without the disclaimers attached. No paid ads on Meta or Google without our approval. No spam, no DM blasts, no purchased lists.
      </Section>
      <Section heading="What you'll get">
        Your referral link, a content library (playbooks, hooks, sample posts, FAQs), early access to product launches, and a real human reviewing your monthly performance. We're not the kind of program that sends you a link and never speaks to you again.
      </Section>
      <Section heading="FAQ — quick hits">
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          <li><strong>How long does review take?</strong> 5 business days.</li>
          <li><strong>Can I use VOYA9 myself?</strong> Yes, and we'd encourage it. But you can't earn commission on your own purchase.</li>
          <li><strong>Tax forms?</strong> W-9 (US) or W-8BEN (non-US) — collected before your first payout.</li>
          <li><strong>Can I be removed?</strong> Yes — for any of the "can't do" violations above, or for inactivity over 6 months.</li>
        </ul>
      </Section>
      <p style={{ marginTop: 28, fontSize: 12, color: C.warmGray }}>
        Full program terms live at <Link to="/legal/terms" style={{ color: C.peachDeep }}>/legal/terms</Link>.
      </p>
    </div>
  );
}

function BrandVoiceAgreementContent() {
  const Section = ({ heading, children }) => (
    <section style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: C.peachDeep, marginBottom: 10 }}>
        ★ {heading.toUpperCase()}
      </h3>
      <div style={{ fontSize: 14.5, lineHeight: 1.65, color: C.ink }}>{children}</div>
    </section>
  );
  return (
    <div>
      <p style={{ marginTop: 0, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 17, lineHeight: 1.6, color: C.warmGray }}>
        How we talk, and how we'd love you to talk about us.
      </p>
      <Section heading="Voice">
        Confident, plain-spoken, never glossy. We don't sell. We notice. We compare honestly — including to ourselves last year. We use editorial italics when we want to slow the reader down.
      </Section>
      <Section heading="Tone tiers">
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          <li><strong>Default:</strong> warm, specific, occasionally dry.</li>
          <li><strong>Compliance topics</strong> (compounded GLP-1, hormone work): clinical, calm, no superlatives.</li>
          <li><strong>Community wins:</strong> celebratory but specific. "Lost 14lbs" beats "transformed."</li>
        </ul>
      </Section>
      <Section heading="Words we use">
        <em>Protocol, member, care team, program, compound, dose, physician.</em> Never "patient" (we're not a clinic in the institutional sense), never "subscription" (we're a program), never "drug" (we're compounds + lifestyle).
      </Section>
      <Section heading="Disclosure">
        Always tag posts <code style={{ background: C.bone, padding: "1px 6px", fontSize: 13 }}>#voya9partner</code> or <code style={{ background: C.bone, padding: "1px 6px", fontSize: 13 }}>#ad</code>. Plain-language disclosure ("I get paid if you sign up — but I only recommend things I use") outperforms FTC-template legalese.
      </Section>
      <Section heading="The agreement">
        By accepting your acceptance email, you agree to: (1) the program terms at <Link to="/legal/terms" style={{ color: C.peachDeep }}>/legal/terms</Link>, (2) the FTC endorsement guidelines, (3) the compliance rules above, and (4) the Brand Voice tiers when posting about us.
      </Section>
      <p style={{ marginTop: 28, fontSize: 12, color: C.warmGray }}>
        Questions before you're approved? Email <a href="mailto:affiliates@voya9.com" style={{ color: C.peachDeep }}>affiliates@voya9.com</a>.
      </p>
    </div>
  );
}

/* ============================================================= */
/*  Validation & helpers                                          */
/* ============================================================= */
function validateStep(step, data) {
  if (step === 1) {
    if (!data.firstName || data.firstName.trim().length < 2) return "Please enter your first name.";
    if (!data.lastName || data.lastName.trim().length < 2) return "Please enter your last name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return "Please enter a valid email address.";
    return null;
  }
  if (step === 2) {
    if (!data.audienceSize) return "Please select your audience size.";
    if ((data.primaryPlatforms || []).length === 0) return "Pick at least one platform.";
    if ((data.audienceTopics || []).length === 0) return "Pick at least one topic.";
    return null;
  }
  if (step === 3) {
    const anyHandle = Object.values(data.socialHandles || {}).some((v) => v && v.trim().length > 0);
    if (!anyHandle) return "Please add at least one social handle or URL.";
    // Content samples are optional — many creators operate exclusively
    // in social where individual posts are hard to deep-link to. The
    // handles in the section above already give the reviewer a place
    // to see the body of work.
    return null;
  }
  if (step === 4) {
    const why = (data.whyVoya9 || "").trim();
    if (why.length < 1) return "Please share why you're interested in VOYA9.";
    if (countWords(data.whyVoya9) > 500) return "Keep 'Why VOYA9' under 500 words.";
    if (!data.promotionPlan || data.promotionPlan.trim().length < 1) return "Please share a quick note on how you'd promote VOYA9.";
    if (!data.termsAccepted) return "Please agree to the affiliate program terms.";
    if (!data.expectationsAcknowledged) return "Please acknowledge the review window.";
    if (TURNSTILE_SITE_KEY && !data.turnstileToken) return "Please complete the human verification.";
    return null;
  }
  return null;
}

function countWords(s) {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}

// Turn an error from `affPost` into a member-readable message. Handles
// the three common error shapes: AffiliateApiError with FastAPI's
// validation `detail` array (422), AffiliateApiError with a plain
// string `detail` (400/401/403/404), and unknown/network errors.
function formatApiError(err) {
  if (!err) return "Something went wrong. Please try again.";
  const detail = err.detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors. Pull the most useful message from the
    // first entry — the BE's loc array points at the offending field.
    const first = detail[0] || {};
    const field = Array.isArray(first.loc) ? first.loc.filter((x) => x !== "body").join(".") : null;
    const msg = first.msg || first.message || "Invalid value";
    return field ? `${field}: ${msg}` : msg;
  }
  if (typeof detail === "string" && detail.trim()) return detail;
  if (err.message && typeof err.message === "string") return err.message;
  return "Something went wrong. Please try again.";
}
